import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdDragIndicator, MdImage, MdFilterList, MdClose } from 'react-icons/md';
import { VariationsSection_AdFragment } from '../../../codegen/graphql';
import { TranslatedValue, Translations, useTranslationEdits } from '../common/TranslatedValue';
import Button from '../common/Button';
import { useImageTools } from '../ImageTools/ImageProvider';

interface SortableTypeValueProps {
  value: VariationsSection_AdFragment['variationTypes'][0]['typeValues'][0];
  type: VariationsSection_AdFragment['variationTypes'][0];
  adData: VariationsSection_AdFragment;
  activeFilters: Array<{ typeId: string; valueId: string }>;
  onChangeTypeValue: (typeId: string, typeValueId: string, value: Translations) => void;
  onImageSelection: (images: VariationsSection_AdFragment['medias'], valueId: string) => void;
  onAddFilter: (typeId: string, valueId: string) => void;
  onRemoveFilter: (filter: { typeId: string; valueId: string }) => void;
  onDeleteValue: (value: any) => void;
}

export function SortableTypeValue({
  value,
  type,
  adData,
  activeFilters,
  onChangeTypeValue,
  onImageSelection,
  onAddFilter,
  onRemoveFilter,
  onDeleteValue,
}: SortableTypeValueProps) {
  const { openImageSelector } = useImageTools();
  const translations = value.value as unknown as Translations;
  const translationEdits = useTranslationEdits(translations);
  const isFiltered = activeFilters.some(f => f.typeId === type.id && f.valueId === value.id);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: value.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-12 gap-2 px-2 py-2 hover:bg-gray-50"
    >
      {/* Drag Handle */}
      <div className="col-span-1 flex items-center justify-center">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
        >
          <MdDragIndicator size={18} className="text-gray-400" />
        </div>
      </div>

      {/* Value Input */}
      <div className="col-span-5">
        <TranslatedValue
          translations={translationEdits.value}
          renderAs="input"
          size="lg"
          onEditTranslation={(updates) => onChangeTypeValue(type.id, value.id, translationEdits.edit(updates))}
        />
      </div>

      {/* Images Section */}
      {type.id === adData.typeIdUsedForImages && (
        <div className="flex flex-row flex-wrap gap-2 items-start col-span-5">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              openImageSelector({
                images: adData.medias,
                initialSelection: value.medias,
                onSelectionComplete: (images) => {
                  onImageSelection(images, value.id);
                }
              });
            }}
            size="sm"
            preset="white"
          >
            <MdImage size={16} />
            <span>{value.medias?.length || 0} images</span>
          </Button>

          {value.medias?.length > 0 && value.medias.map((media) => (
            <img key={media.id} src={media.url} className="border-1 border-gray-200 w-16 h-16 object-cover rounded-md" />
          ))}
        </div>
      )}

      {/* Actions */}
      <div className={`${type.id === adData.typeIdUsedForImages ? 'col-span-1' : 'col-span-6'} flex flex-col items-stretch`}>
        <Button
          onClick={() => {
            if (isFiltered) {
              onRemoveFilter({ typeId: type.id, valueId: value.id })
            } else {
              onAddFilter(type.id, value.id)
            }
          }}
          size="sm"
          preset={isFiltered ? "blue" : "white"}
          className="flex justify-center"
        >
          <MdFilterList size={18} />
        </Button>
        <Button
          onClick={() => onDeleteValue(value)}
          preset="danger-light"
          className="flex justify-center"
        >
          <MdClose size={18} />
        </Button>
      </div>
    </div>
  );
}
